import { useEffect, useRef, useState } from "react"
import { AsyncStatus, StageState, UpdateState } from "enums"
import { IGetAffectedResponse, parseAxiosError, PubSub} from "services";
import { assertUnreachable, Stale } from "services/Utils";
import { DateTime } from "luxon";
import { ILoadResult, LoaderAllDevices, LoaderUpdate } from "./Loaders";
import { ReportsFieldType } from "./enums/ReportsFieldType";
import { ReportsFilterType } from "./enums/ReportsFilterType";
import { TableId } from "./enums/TableId";
import { URLParam } from "./enums/URLParam";
import { DataSource } from "./enums/DataSource";
import { IUpdate } from "interfaces";

// If we ever change any of the filters/tables/fields, then an old url won't work correctly,
// or may have missing params. We're not doing full migration for all previous urls,
// but we'll try to fill in any missing params with sane defaults.
const URLFormatVersion = '2';

// Some default structs to help with column picking
// these correlate to the field IDs from Loaders.deviceFields
export const defaultVisibleColumns = {
    manufacturerName: true,
    modelName: true,
    location: true,
    locationName: true,
    locationAddress: true,
    locationCity: true,
    locationState: true,
    locationType: true,
    serialNumber: true,
    thingId: true,
    lastSeen: true,
    firmwareVersion: true,
    menuVersion: true,
    macAddress: true,
    deviceState: true,
    deviceStateDetail: true,
    updateId: true,
    currentVersion: true,
    componentType: true,
    componentKey: true,
    componentVersion: true,
}

export const defaultHiddenColumns = {
    manufacturerName: false,
    modelName: false,
    location: false,
    locationName: false,
    locationAddress: false,
    locationCity: false,
    locationState: false,
    locationType: false,
    serialNumber: false,
    thingId: false,
    lastSeen: false,
    firmwareVersion: false,
    macAddress: false,
    menuVersion: false,
    deviceState: false,
    deviceStateDetail: false,
    updateId: false,
    currentVersion: false,
    componentType: false,
    componentKey: false,
    componentVersion: false,
}

export interface IField {
    id: string,
    isGeneratedAggregate?: boolean, // True for aggregate fields like "(Total)"
    isGroupable: boolean,
    isSortable: boolean,
    label: string,
    propName: string,
    type: ReportsFieldType,
}

export interface IStringFilter {
    type: ReportsFilterType.String,
    comparator: 'equal' | 'in' | 'contains' | 'startsWith',
    fieldId: string,
    id: string,
    includeEmptyValues?: boolean,
    negateComparison: boolean,
    value: string | string[],
}

export interface IDateFilter {
    comparator: '<' | '>',
    fieldId: string,
    id: string,
    type: ReportsFilterType.Date,
    value: DateTime,
}

type IDateFilterURL = Modify<IDateFilter, {
    value: string,
}>

export interface INumberFilter {
    comparator: '<' | '<=' | '>' | '>=' | '=',
    fieldId: string,
    id: string,
    type: ReportsFilterType.Number,
    value: number,
}

export type IReportsFilter = IStringFilter | IDateFilter | INumberFilter;
type IReportsFilterURL = IStringFilter | IDateFilterURL | INumberFilter;

export interface ISortBy {
    ascending: boolean,
    fieldId: string,
}

export interface IGroupBy {
    fieldId: string,
}

export interface ITable {
    fields: IField[],
    id: TableId,
    label: string,
}

export type IRow = {
    [propName: string]: string | number | DateTime,
    __rowId: string,
};

// We can optionally use an update as our data source instead of showing all devices.
// These params control which part of the update we show.
// We default to showing devices from all stages.
export interface IUpdateParams {
    filterId?: number | undefined,
    isGlobalFilters?: boolean,
    isSnapshot?: boolean,
    stageIndex?: number | undefined,
    updateId?: number,
}

interface IReportState {
    dataSource: DataSource,
    updateParams?: IUpdateParams,
    tableId: TableId,
    filters: IReportsFilter[],
    groupBy: IGroupBy[],
    sortBy: ISortBy[],
}

const totalField: IField = {
    id: 'computed_total',
    propName: '__computed_total',
    label: '(Total)',
    type: ReportsFieldType.Number,
    isGeneratedAggregate: true,
    isGroupable: false,
    isSortable: true,
}

// Every filter needs a unique id so that react can render it in a list.
// For other things like groups and sorts, we're using fieldId because it's unique within that list.
let curFilterId = 1;
export function generateReportsFilterId() {
    const id = `filter_${curFilterId}`;
    curFilterId++;
    return id;
}

// These functions are so that we generate the same update config  whether we're changing a select in reports,
// or clicking the "View Results" button on an update.
export function getDefaultUpdateParams_update(updateId: number, updateState: UpdateState): IUpdateParams {
    return {
        updateId: updateId,
        isSnapshot: updateState >= UpdateState.Running,
    }
}

export function getDefaultUpdateParams_stage(updateId: number, stageIndex: number, stageState: StageState): IUpdateParams {
    return {
        updateId: updateId,
        stageIndex: stageIndex,
        isSnapshot: stageState >= StageState.Running,
    }
}

export function getUpdateResultsURL(updateParams: IUpdateParams): string {
    const params = new URLSearchParams();
    params.append(URLParam.Version, URLFormatVersion);
    params.append(URLParam.DataSource, DataSource.Update);
    params.append(URLParam.UpdateParams, JSON.stringify(updateParams));
    params.append(URLParam.Table, TableId.Devices);
    return '/reports?' + params.toString();
}

export class ReportsModel {
    // When something changes, we'll publish an event. Hooks can use this to trigger a rerender.
    public pubSubOnChange = new PubSub<void>();
    
    // Loaders for each use case. We can show data from several different sources.
    private loaderAllDevices = new LoaderAllDevices();
    private loaderUpdate = new LoaderUpdate();
    
    // Data Loading
    public loadStatus = AsyncStatus.Initial;
    public loadError = '';
    public dataModifiedAtDt: DateTime;
    public allTables: ITable[] = [];
    public allTablesById: {[tableId: string]: ITable} = {};
    public allFieldsById: {[fieldId: string]: IField} = {};
    public allRowsByTableId: {[tableId: string]: IRow[]} = {};

    // User Inputs
    public dataSource: DataSource;
    public updateParams: IUpdateParams = {};
    public activeTableId: TableId;
    public filters: IReportsFilter[] = [];
    public groupBy: IGroupBy[] = [];
    public sortBy: ISortBy[] = [];
    public visibleColumns: {[id: string]: boolean} = {};

    // Outputs
    public outFields: IField[] = [];
    public outFieldsById: {[fieldId: string]: IField} = {};
    public outRows: IRow[] = [];
    public outUpdate: IUpdate;
    public outAffected: IGetAffectedResponse;
    // Sometimes we want to show an message if the user selects an unsupported configuration,
    // like viewing a snapshot for a filter. We don't want to style that red like other errors,
    // so t's easier to keep that logic in the loader and return a custom error message.
    public outCustomError: string;

    private loadStale = new Stale();
    public load = (timezone: string): Promise<any> => {
        this.loadStatus = AsyncStatus.Loading;
        this.loadError = '';
        const loadId = this.loadStale.startCall();

        let p: Promise<ILoadResult>;
        switch (this.dataSource) {
            case DataSource.AllDevices:
                p = this.loaderAllDevices.load(timezone);
                break;
            case DataSource.Update:
                p = this.loaderUpdate.load(this.updateParams, timezone);
                break;
            default:
                p = Promise.reject('Unsupported data source: ' + this.dataSource);
                break;
        }

        return p.then(({ tables, rowsByTableId, modifiedAt, update, affected, customError }) => {
            if (this.loadStale.isStale(loadId)) return;

            this.loadStatus = AsyncStatus.Success;
            this.outUpdate = update;
            this.outAffected = affected;
            this.outCustomError = customError;
            this.allRowsByTableId = rowsByTableId;
            this.dataModifiedAtDt = DateTime.fromJSDate(modifiedAt, { zone: timezone });

            this.resetTablesAndFields(tables);
            this.runReport();
        }, (err) => {
            if (this.loadStale.isStale(loadId)) return;

            this.loadStatus = AsyncStatus.Error;
            this.loadError = parseAxiosError(err);
            
            this.pubSubOnChange.publish();
            throw err;
        });
    }

    private resetTablesAndFields(tables: ITable[]) {
        this.allTables = tables;
        this.allTablesById = {};
        this.allFieldsById = {};
        this.visibleColumns = defaultVisibleColumns;

        for (const t of tables) {
            this.allTablesById[t.id] = t;

            for (const f of t.fields) {
                this.allFieldsById[f.id] = f;
            }
        }

        // Be sure to include aggregate functions. These are only present if the user applies grouping.
        this.allFieldsById[totalField.id] = totalField;
    }

    public setUpdateParams(params: IUpdateParams, timezone: string) {
        this.updateParams = params;
        this.load(timezone);
    }

    public removeUpdateParams(timezone: string) {
        this.dataSource = DataSource.AllDevices;
        this.updateParams = {};
        this.load(timezone);
    }

    public setActiveTableId(tableId: TableId) {
        this.activeTableId = tableId;
        this.runReport();
    }

    public setFilters(newFilters: IReportsFilter[]) {
        this.filters = newFilters;
        this.runReport();
    }

    public setGroupBy(newGroupBy: IGroupBy[]) {
        this.groupBy = newGroupBy;

        // If we remove the last groupBy, we need to remove aggregate filters
        const allowAggregate = this.groupBy.length > 0;
        if (!allowAggregate) {
            this.filters = this.filters.filter(filter => {
                const field = this.allFieldsById[filter.fieldId];
                return !field.isGeneratedAggregate;
            })
        }

        this.runReport();
    }

    public setSortBy(newSortBy: ISortBy[]) {
        this.sortBy = newSortBy;
        this.runReport();
    }

    public setVisibleColumns(newVisibleColumns: {[id: string]: boolean}) {
        this.visibleColumns = newVisibleColumns;
        this.runReport();
    }

    public runReport = () => {
        if (this.loadStatus !== AsyncStatus.Success) return;
        this.removeInvalidGroupsAndFilters();

        const table = this.allTablesById[this.activeTableId];
        if (!table) {
            // We won't have a table if the user tries to view a snapshot for a stage that hasn't started yet.
            this.outFields = [];
            this.outFieldsById = {};
            this.outRows = [];
            this.pubSubOnChange.publish();
            return;
        }

        // Initialize fields and rows
        let fields = table.fields.filter(f => (this.visibleColumns[f.id]));
        let rows: IRow[] = this.allRowsByTableId[this.activeTableId] || [];

        // Apply filters (excluding post-grouping filters)
        const normalFilters = this.filters.filter(f => {
            const field = this.allFieldsById[f.fieldId];
            return !field.isGeneratedAggregate;
        });
        rows = this.filterRows(rows, normalFilters);
        
        // Group
        const groupResult = this.groupRows(fields, rows, this.groupBy);
        rows = groupResult.rows;
        fields = groupResult.fields;

        // Apply post-grouping filters
        const postGroupingFilters = this.filters.filter(f => {
            const field = this.allFieldsById[f.fieldId];
            return field.isGeneratedAggregate;
        })
        rows = this.filterRows(rows, postGroupingFilters);

        // Only include sorts that are for fields in the output table.
        // For example, if we're only grouping by "Manufacturer", we can't sort by "Model".
        const outFieldIDs = {};
        for (const f of fields) {
            outFieldIDs[f.id] = true;
        }
        this.sortBy = this.sortBy.filter(s => outFieldIDs[s.fieldId]);

        // Sort
        this.sortRows(rows, this.sortBy);

        this.outFields = fields;
        this.outFieldsById = {};
        for (const f of fields) {
            this.outFieldsById[f.id] = f;
        }
        this.outRows = rows;
        this.pubSubOnChange.publish();
    }

    // Removes any filters or groups that aren't for the active table.
    private removeInvalidGroupsAndFilters() {
        const table = this.allTablesById[this.activeTableId];
        if (!table) {
            this.groupBy = [];
            this.filters = [];
            this.sortBy = [];
            if (this.allTables.length > 0) {
                this.activeTableId = this.allTables[0].id;
            }
            return;
        }

        // Groups
        this.groupBy = this.groupBy.filter(g => {
            const field = this.allFieldsById[g.fieldId];
            return field && field.isGroupable;
        })

        // Filters
        const canIncludeAggregate = this.groupBy.length > 0;
        this.filters = this.filters.filter(f => {
            const field = this.allFieldsById[f.fieldId];
            return field && (!field.isGeneratedAggregate || canIncludeAggregate)
        })
    }

    public getSettingsAsURL(): string {
        const params = new URLSearchParams();
        params.append(URLParam.Version, URLFormatVersion);
        params.append(URLParam.DataSource, this.dataSource);
        params.append(URLParam.Table, this.activeTableId);
        // Using try/catch because JSON can throw.
        try {
            params.append(URLParam.UpdateParams, JSON.stringify(this.updateParams));

            if (this.groupBy.length > 0) {
                params.append(URLParam.GroupBy, JSON.stringify(this.groupBy));
            }
            if (this.sortBy.length > 0) {
                params.append(URLParam.SortBy, JSON.stringify(this.sortBy));
            }
            if (this.filters.length > 0) {
                const filtersURL = this.filters.map(this.mapFilterToURL)
                params.append(URLParam.Filters, JSON.stringify(filtersURL));
            }
        } catch (err) {
            console.error('Error serializing report settings: ', err)
        }

        return params.toString();
    }

    public resetFromURL(url: string, timezone: string) {
        // Reset everything so that we start fresh. We don't want to carry over stuff that isn't valid
        // for the current table.
        this.updateParams = {};
        this.groupBy = [];
        this.sortBy = [];
        this.filters = [];

        const params = new URLSearchParams(url);

        this.dataSource = params.get(URLParam.DataSource) as DataSource || DataSource.AllDevices;
        this.activeTableId = params.get(URLParam.Table) as TableId || TableId.Devices;

        const updateParamsJSON = params.get(URLParam.UpdateParams);
        const groupByJSON = params.get(URLParam.GroupBy);
        const sortByJSON = params.get(URLParam.SortBy);
        const filtersJSON = params.get(URLParam.Filters);

        if (updateParamsJSON) {
            // Using try/catch here to catch url errors. Also, if the user has a lot of filter data,
            // it's possible they'll hit browser url character length limits.
            try {
                this.updateParams = JSON.parse(updateParamsJSON);
            } catch (err) {
                console.error('Error parsing updateParams JSON: ', err);
            }
        }
        if (groupByJSON) {
            try {
                this.groupBy = JSON.parse(groupByJSON);
            } catch (err) {
                console.error('Error parsing groupBy JSON: ', err);
            }
        }
        if (sortByJSON) {
            try {
                this.sortBy = JSON.parse(sortByJSON);
            } catch (err) {
                console.error('Error parsing sortBy JSON: ', err);
            }
        }
        if (filtersJSON) {
            try {
                const filtersURL: IReportsFilterURL[] = JSON.parse(filtersJSON);
                this.filters = filtersURL.map(f => this.mapFilterFromURL(f, timezone));
                // Our id generator starts at 1, the id's may conflict, so we'll remap them.
                for (const f of this.filters) {
                    f.id = generateReportsFilterId();
                }
            } catch (err) {
                console.error('Error parsing filters JSON: ', err);
            }
        }

        this.pubSubOnChange.publish();
    }

    public getFiltersFromGroupBys = (groupBys: IGroupBy[], row: IRow): IReportsFilter[] => {
        return groupBys.map((groupBy) => this.getFilterFromGroupBy(groupBy, row));
    }

    private getFilterFromGroupBy = (groupBy: IGroupBy, row: IRow): IReportsFilter => {
        const field = this.allFieldsById[groupBy.fieldId];
        if (!field) {
            console.error(`Missing field for groupBy field: ` + groupBy.fieldId);
            return undefined;
        }

        switch (field.type) {
            case ReportsFieldType.String:
                return {
                    id: generateReportsFilterId(),
                    type: ReportsFilterType.String,
                    fieldId: groupBy.fieldId,
                    comparator: 'equal',
                    negateComparison: false,
                    value: row[field.propName] + '',
                }
            case ReportsFieldType.Number:
                return {
                    id: generateReportsFilterId(),
                    type: ReportsFilterType.Number,
                    fieldId: groupBy.fieldId,
                    comparator: '=',
                    value: row[field.propName] as number,
                }
            case ReportsFieldType.Date:
                console.error(`Date filters aren't groupable, can't convert to filter.`)
                return undefined;
        }
        return assertUnreachable(field.type);
    }

    public createBlankFilter(field: IField): IReportsFilter {
        switch (field.type) {
            case ReportsFieldType.String:
                return {
                    type: ReportsFilterType.String,
                    fieldId: field.id,
                    id: generateReportsFilterId(),
                    comparator: 'in',
                    negateComparison: false,
                    value: [],
                };
            case ReportsFieldType.Date:
                return {
                    type: ReportsFilterType.Date,
                    fieldId: field.id,
                    id: generateReportsFilterId(),
                    comparator: '>',
                    value: DateTime.now(),
                };
            case ReportsFieldType.Number:
                return {
                    type: ReportsFilterType.Number,
                    fieldId: field.id,
                    id: generateReportsFilterId(),
                    comparator: '>',
                    value: 1,
                };
        }
        return assertUnreachable(field.type);
    }

    private mapFilterFromURL(f: IReportsFilterURL, timezone: string): IReportsFilter {
        switch (f.type) {
            case ReportsFilterType.String:
            case ReportsFilterType.Number:
                return f;
            case ReportsFilterType.Date:
                // Need to parse date from string
                const newFilter: IDateFilter = {
                    ...f,
                    value: f.value ? DateTime.fromISO(f.value, {zone: timezone}) : undefined,
                };
                return newFilter;
        }
        return assertUnreachable(f);
    }

    private mapFilterToURL(f: IReportsFilter): IReportsFilterURL {
        switch (f.type) {
            case ReportsFilterType.String:
            case ReportsFilterType.Number:
                return f;
            case ReportsFilterType.Date:
                // Convert date to string
                const newFilter: IDateFilterURL = {
                    ...f,
                    value: f.value ? f.value.toISO() : null,
                };
                return newFilter;
        }
        return assertUnreachable(f);
    }

    private filterRows = (rows: IRow[], filters: IReportsFilter[]): IRow[] => {
        for (const filter of filters) {
            // Generate a filter function depending on what type of filter this is.
            const filterFunc = this.createFilterFunction(filter, rows);
            rows = rows.filter(filterFunc)
        }
        return rows;
    }

    private createFilterFunction(filter: IReportsFilter, allRows: IRow[]): (row: IRow) => boolean {
        const filterField = this.allFieldsById[filter.fieldId];
        if (!filterField) {
            console.error('missing field for filter.fieldId: ' + filter.fieldId);
            return () => true;
        }

        const funcLessThan = (row) => row[filterField.propName] < filter.value;
        const funcGreaterThan = (row) => row[filterField.propName] > filter.value;
        const funcEqual = (row) => row[filterField.propName] === filter.value;
        const funcNotEqual = (row) => row[filterField.propName] !== filter.value;

        switch (filter.type) {
            case ReportsFilterType.String:
                switch (filter.comparator) {
                    case 'equal':
                        return filter.negateComparison ? funcNotEqual : funcEqual;
                    case 'contains': {
                        const lowerFilterVal = (filter.value as string).toLowerCase();
                        return (row) => {
                            const rowVal = row[filterField.propName] as string;
                            if (!rowVal.includes) return false;

                            const contains = rowVal.toLowerCase().includes(lowerFilterVal);
                            return filter.negateComparison ? !contains : contains;
                        }
                    }
                    case 'in': {
                        // Precompute lookup table so that we avoid O(n^2) run times.
                        const filterVals = {};
                        for (const val of filter.value) {
                            filterVals[val.toLowerCase()] = true;
                        }

                        return (row) => {
                            const rowVal = row[filterField.propName] as string;
                            const isIn = !!filterVals[rowVal.toLowerCase()];
                            return filter.negateComparison ? !isIn : isIn;
                        }
                    }
                    case 'startsWith': {
                        const lowerFilterVal = (filter.value as string).toLowerCase();
                        return (row) => {
                            const rowVal = row[filterField.propName] as string;
                            if (!rowVal.startsWith) return false;

                            const startsWith = rowVal.toLowerCase().startsWith(lowerFilterVal);
                            return filter.negateComparison ? !startsWith : startsWith;
                        }
                    }
                }
            case ReportsFilterType.Date: {
                switch (filter.comparator) {
                    case '<':
                        return funcLessThan;
                    case '>':
                        return funcGreaterThan;
                }
            }
            case ReportsFilterType.Number:
                switch (filter.comparator) {
                    case '<':
                        return funcLessThan;
                    case '<=':
                        return (row) => row[filterField.propName]+'' <= filter.value+'';
                    case '>':
                        return funcGreaterThan;
                    case '>=':
                        return (row) => row[filterField.propName] +'' >= filter.value+'';
                    case '=':
                        return funcEqual;
                }
        }
        return assertUnreachable(filter);
    }

    private groupRows(fields: IField[], rows: IRow[], groupBy: IGroupBy[]): {rows: IRow[], fields: IField[]} {
        // Group
        if (groupBy.length > 0) {
            // Grouping changes the fields. Only fields in the group list are included, plus a new "computed_count" field.
            const groupByFields: IField[] = [];
            for (const group of groupBy) {
                const field = this.allFieldsById[group.fieldId];
                if (!field) {
                    console.error('Missing groupBy field: ' + group.fieldId);
                    return {rows: rows, fields: fields};
                }

                groupByFields.push(field);
            }

            // To group, we need to get the count of all devices with the same values for all grouping fields.
            // For example, if we're grouping by manufacturer and model, devices would need to have
            // the same manufacturer and model to be included in the same grouped row.
            const groupedRowsByVal: {[groupedVal: string]: IRow} = {};
            for (const row of rows) {
                // Get the values for the group fields and concat them to get a combined value
                let rowVal = '';
                for (const field of groupByFields) {
                    rowVal += row[field.propName] + '_'; // Use a separator so the values don't get mixed
                }

                // Now that we have a value for this row, see if we already have a grouped row for it.
                let groupedRow = groupedRowsByVal[rowVal];
                if (groupedRow) {
                    // We already have a grouped row, increment the count. 
                    // Typescript doesn't know this field is a number, so we have to cast.
                    (groupedRow[totalField.propName] as number)++;
                } else {
                    // Need to create a row
                    const newGroupedRow: IRow = {
                        [totalField.propName]: 1,
                        __rowId: 'grouped_' + rowVal,
                    }

                    // Copy over values into the grouped row
                    for (const field of groupByFields) {
                        newGroupedRow[field.propName] = row[field.propName];
                    }

                    // Add the new grouped row to our map
                    groupedRowsByVal[rowVal] = newGroupedRow;
                }
            }

            // We've grouped all rows and gotten a count for each group.
            // Now, we need to turn the grouped rows object back into an array of rows.
            return {
                fields: [
                    ...groupByFields,
                    totalField,
                ],
                rows: Object.values(groupedRowsByVal),
            }
        }
        return {rows: rows, fields: fields};
    }

    // Case insensitive natural sort
    private sortStringCollator = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});

    private sortRows(rows: IRow[], sorts: ISortBy[]) {
        // Iterate list of sorts backwards, so that sorts earlier in the list have highest priority.
        for (let i = sorts.length - 1; i >= 0; i--) {
            const sort = sorts[i];
            const compareFunc = this.createCompareFunction(sort);
            rows.sort(compareFunc);
        }
    }

    // Create a function to compare rows.
    // NOTE: This is performance sensitive code. We want to do as little inside the comparator as possible,
    // so that it's fast.
    private createCompareFunction = (sort: ISortBy): (a: IRow, b: IRow) => number => {
        const field = this.allFieldsById[sort.fieldId];
        if (!field) {
            console.error(`Missing field for sort: ` + sort.fieldId);
            return (a, b) => 0;
        }
        const propName = field.propName;

        switch (field.type) {
            case ReportsFieldType.Number:
                if (sort.ascending) {
                    return (a: IRow, b: IRow) => {
                        return (a[propName] as number) - (b[propName] as number);
                    };
                } else {
                    return (a: IRow, b: IRow) => {
                        return (b[propName] as number) - (a[propName] as number);
                    };
                }
            case ReportsFieldType.Date:
                if (sort.ascending) {
                    return (a: IRow, b: IRow) => {
                        return (a[propName] as DateTime).valueOf() - (b[propName] as DateTime).valueOf();
                    };
                } else {
                    return (a: IRow, b: IRow) => {
                        return (b[propName] as DateTime).valueOf() - (a[propName] as DateTime).valueOf();
                    };
                }
            case ReportsFieldType.String:
                if (sort.ascending) {
                    return (a: IRow, b: IRow) => {
                        return this.sortStringCollator.compare(a[propName] as string, b[propName] as string);
                    }
                } else {
                    return (a: IRow, b: IRow) => {
                        return this.sortStringCollator.compare(b[propName] as string, a[propName] as string);
                    }
                }
        }
        return assertUnreachable(field.type);
    }
}

// Wraps a ReportsModel in hooks so that when something changes, the component will rerender.
export function useReportsModel(): {model: ReportsModel} {
    const modelRef = useRef<ReportsModel>();
    if (!modelRef.current) {
        modelRef.current = new ReportsModel();
    }
    const model = modelRef.current;

    // Instead of creating hooks versions of all the ReportModel methods,
    // we'll just use the model as the source of truth, and set our state when it changes, which will cause a rerender.
    const [state, setState] = useState({model: model});
    
    useEffect(() => {
        const unsubscribe = model.pubSubOnChange.subscribe(() => {
            setState({model: model});
        })

        return unsubscribe;
    });

    return {model: model};
}